<script>
import { termsStore } from '../../stores/termsStore.js';
import { validationCacheStore } from '../../stores/validationCacheStore.js';
import { sortedTerms, filteredTerms } from '../../stores/derivedStores.js';
import ViewFilterDropdown from './ViewFilterDropdown.svelte';
import { specStore, claimsStore } from '../../stores/editorsStore.js';
import { fade, scale, slide } from 'svelte/transition';
import { termChildService } from '../../services/termChildService.js';
import { backendService } from '../../services/backendService.js';
import { ClaimsTermUpdateService } from '../../services/claimsTermUpdateService.js';
import { PersistentTermsService } from '../../services/persistentTermsService.js';
import { settingsStore, termsSettings } from '../../stores/settingsStore.js';
import { manualTermsServiceInstance } from '../../services/manualTermsService';
import { getGlobalDocumentId } from '../../services/persistentTermsService.js';
import { NOTIFICATION_TIMEOUT_MS } from '../../constants/ui.js';
import NotificationBubble from '../shared/NotificationBubble.svelte';
import ThreadContainer from './ThreadContainer.svelte';
import { termAutoScrollService } from '../../services/termAutoScrollService.js';
import { onDestroy } from 'svelte';
import TermProgressBar from './TermProgressBar.svelte';

// Thread styling variables are now in global.css

// Initialize claims term update service
let claimsTermService = new ClaimsTermUpdateService(termsStore, claimsStore);

// Initialize persistent terms service
let persistentTermsService = new PersistentTermsService(termsStore, claimsStore, backendService);

// Cleanup on component destroy
onDestroy(() => {
    if (claimsTermService) {
        claimsTermService.destroy();
    }
});

// Reactive declarations for indexed term entries using derived store for sorting and filtering
$: termEntries = $filteredTerms; // Already sorted and filtered array of [indexedKey, termData] pairs
$: totalTermCount = $sortedTerms.length; // Total terms before filtering
$: filteredTermCount = $filteredTerms.length; // Terms after filtering
$: currentViewFilter = $termsStore.viewFilter || 'all';
$: selectedEntries = $termsStore.selectedEntries;
$: highlightedEntries = $termsStore.highlightedEntries;
$: editingEntry = $termsStore.editingEntry;
$: console.log(`[TermsPanel] editingEntry changed to:`, editingEntry);
$: isTermAddingMode = $termsStore.isTermAddingMode;
$: expandedEntries = $termsStore.expandedEntries || new Set();
$: currentSortMode = $termsStore.sortMode || 'appearance';

// Always show claim reference numbers for better context
const showClaimReferences = true;

// Sort menu state
let showSortMenu = false;

// Notification state for refresh button
let currentNotification = null;
let notificationTimeout;
let notificationSource = null;

// Position tracking for Add button notification
let addButtonElement = null;
let notificationStyle = '';

// Flag to prevent blur from interfering with save
let isSaving = false;

// Store the editing value separately from the reactive termData
let editingValue = '';
$: loadingOperations = $termsStore.loadingOperations || new Map();
$: isAnalyzingTerms = $termsStore.isAnalyzingTerms || false;
$: hasActiveOperations = loadingOperations.size > 0 || isAnalyzingTerms;

// Log when backend operations status changes
$: {
    if (hasActiveOperations) {
        const operations = [];
        if (isAnalyzingTerms) operations.push('Term Analysis');
        loadingOperations.forEach((op, key) => {
            operations.push(op.type || key);
        });
        console.log(`⏳ ACTIVE BACKEND OPERATIONS: ${operations.join(', ')}`);
    } else if (previousHasActiveOperations) {
        console.log('✅ ALL BACKEND OPERATIONS COMPLETED');
    }
    previousHasActiveOperations = hasActiveOperations;
}

let previousHasActiveOperations = false;

// Term progress bar state
let termProgressVisible = false;
let termProgressPercent = 0;
let termProgressStepsRemaining = 0;
let termProgressTotalSteps = 0;
let termProgressEstimatedTime = null;

// Update notification position when isTermAddingMode changes
$: if (isTermAddingMode && addButtonElement) {
    updateNotificationPosition();
} else {
    notificationStyle = '';
}

function updateNotificationPosition() {
    if (!addButtonElement) return;
    
    const buttonRect = addButtonElement.getBoundingClientRect();
    let centerX = buttonRect.left + (buttonRect.width / 2);
    
    // Find the terms toolbar element to position above it
    const termsToolbar = document.querySelector('.terms-toolbar');
    const toolbarRect = termsToolbar ? termsToolbar.getBoundingClientRect() : buttonRect;
    const topY = toolbarRect.top - 40; // 40px above the toolbar
    
    // Estimate tooltip width (approximately 200px for "Term Selection Mode Active")
    const estimatedTooltipWidth = 200;
    const tooltipHalfWidth = estimatedTooltipWidth / 2;
    const viewportWidth = window.innerWidth;
    
    // Adjust position if tooltip would go off-screen
    if (centerX - tooltipHalfWidth < 10) {
        // Too far left - move tooltip right
        centerX = tooltipHalfWidth + 10;
    } else if (centerX + tooltipHalfWidth > viewportWidth - 10) {
        // Too far right - move tooltip left
        centerX = viewportWidth - tooltipHalfWidth - 10;
    }
    
    notificationStyle = `
        position: fixed !important;
        top: ${topY}px !important;
        left: ${centerX}px !important;
        transform: translateX(-50%) !important;
        z-index: 9999 !important;
    `;
}

function handleSortButtonClick(e) {
    e.stopPropagation();
    showSortMenu = !showSortMenu;
}

function handleSortTerms(sortType) {
    console.log(`Sorting terms by: ${sortType}`);
    termsStore.setSortMode(sortType);
}

function handleClickOutside(event) {
    if (!event.target.closest('.sort-menu')) {
        showSortMenu = false;
    }
}

function handleKeydown(e) {
    if (e.key === 'Escape') {
        showSortMenu = false;
    }
}

// Single notification function that replaces any existing notification
function showNotification(message, source = null) {
    // Clear any existing timeout
    if (notificationTimeout) clearTimeout(notificationTimeout);
    
    // Set the new notification
    currentNotification = message;
    notificationSource = source;
    
    // Set timeout to hide notification
    notificationTimeout = setTimeout(() => {
        currentNotification = null;
        notificationSource = null;
    }, NOTIFICATION_TIMEOUT_MS);
}

function handleStopTermAnalysis() {
    console.log('[DEBUG] handleStopTermAnalysis called');
    const wasStopped = backendService.stopTermAnalysis();
    if (wasStopped) {
        console.log('[DEBUG] Term analysis stopped successfully');
        // Hide the progress bar
        termProgressVisible = false;
        // Reset analyzing state
        termsStore.setAnalyzingTerms(false);
        // No notification needed - progress bar disappearing is enough feedback
    } else {
        console.log('[DEBUG] No term analysis was running');
    }
}

async function handleRefreshTerms() {
    console.log('[DEBUG] handleRefreshTerms called from TermsPanel');
    const claims = $claimsStore.content;
    
    // Filter out inline comment lines (starting with #) before checking
    const actualClaims = claims.split('\n')
        .filter(line => !line.trimStart().startsWith('#'))
        .join('\n')
        .trim();
    
    if (!actualClaims) {
        console.log('[DEBUG] No claims found, showing notification');
        showNotification('Please add claims first', 'refresh-terms');
        return;
    }
    
    // Check for validation errors before proceeding
    try {
        const validationResult = await backendService.validateClaims(actualClaims);
        // Cache the validation result for other components to use
        if (validationResult) {
            validationCacheStore.setCachedValidation(claims, validationResult);
        }
        
        if (validationResult?.validation_errors) {
            // Update the claims editor with validation errors
            if (window.updateClaimsValidationErrors) {
                window.updateClaimsValidationErrors(validationResult.validation_errors);
            }
            
            const hasBlockingError = validationResult.validation_errors.some(err => err.blocking);
            if (hasBlockingError) {
                showNotification('Blocking errors. Check errors and warnings', 'refresh-terms');
                return;
            }
        }
    } catch (error) {
        console.error('Error validating claims:', error);
    }
    
    console.log('[DEBUG] Claims found and validated, proceeding with refresh');
    try {
        // Set analyzing state to true
        termsStore.setAnalyzingTerms(true);

        // Count the total number of claims for progress tracking
        const claimLines = actualClaims.split('\n');
        const totalClaims = claimLines.filter(line => {
            const trimmed = line.trim();
            return /^\d+\./.test(trimmed); // Matches lines starting with a number followed by a period
        }).length;

        console.log('[DEBUG] Counted claims:', totalClaims);

        // Show progress bar with initial values
        termProgressVisible = true;
        termProgressPercent = 0;
        termProgressStepsRemaining = totalClaims;
        termProgressTotalSteps = totalClaims;
        termProgressEstimatedTime = null;

        // Track start time for elapsed time calculation
        const startTime = Date.now();

        // Use streaming for manual refresh to show progress
        console.log('[MANUAL_REFRESH] Starting manual refresh with streaming');
        
        // Get manual terms from the store
        const manualTerms = Array.from($termsStore.manualTerms);
        console.log('[MANUAL_REFRESH] Sending manual terms to backend:', manualTerms);
        console.log('[MANUAL_REFRESH] Calling analyzeClaimsText with forceHeavy=true');
        
        // Check patent type and log it
        const patentType = $settingsStore.patentType;
        const isProvisional = patentType === 'provisional';
        console.log('[MANUAL_REFRESH] Patent type:', patentType, 'is_provisional:', isProvisional);
        
        const result = await backendService.analyzeClaimsText(claims, true, {
            useStreaming: true,
            clearBackendState: true,
            manualTerms: manualTerms,
            document_id: getGlobalDocumentId(),
            is_provisional: isProvisional,
            onProgress: (progressData) => {
                console.log('[MANUAL_REFRESH] Progress callback received:', {
                    termCounts: progressData.term_counts ? Object.keys(progressData.term_counts).length : 0,
                    discoveredTerms: progressData.discovered_terms,
                    claimsProcessed: progressData.claims_processed,
                    totalClaims: progressData.total_claims,
                    isComplete: progressData.isComplete,
                    validation_errors: progressData.validation_errors
                });

                // Update progress bar based on backend response
                console.log('[PROGRESS_BAR] Progress data:', {
                    total_claims: progressData.total_claims,
                    claims_processed: progressData.claims_processed,
                    isComplete: progressData.isComplete
                });

                if (progressData.total_claims !== undefined) {
                    termProgressTotalSteps = progressData.total_claims;
                    const claimsProcessed = progressData.claims_processed || 0;
                    termProgressStepsRemaining = Math.max(0, progressData.total_claims - claimsProcessed);
                    termProgressPercent = progressData.total_claims > 0
                        ? Math.round((claimsProcessed / progressData.total_claims) * 100)
                        : 0;

                    console.log('[PROGRESS_BAR] Updated:', {
                        totalSteps: termProgressTotalSteps,
                        stepsRemaining: termProgressStepsRemaining,
                        percent: termProgressPercent
                    });

                    // Calculate estimated time using same approach as spec generation
                    const elapsed = (Date.now() - startTime) / 1000; // seconds
                    const estimatedTotal = claimsProcessed > 0
                        ? (elapsed / claimsProcessed) * progressData.total_claims
                        : null;

                    termProgressEstimatedTime = estimatedTotal ? Math.max(0, estimatedTotal - elapsed) : null;

                    console.log('[PROGRESS_BAR] Time calculation:', {
                        elapsed,
                        estimatedTotal,
                        estimatedRemaining: termProgressEstimatedTime
                    });
                }

                // Check for validation errors
                if (progressData.validation_errors) {
                    console.error('[MANUAL_REFRESH] Validation errors detected:', progressData.validation_errors);
                    
                    // Check if there are blocking errors
                    const hasBlockingError = progressData.validation_errors.some(err => err.blocking);
                    if (hasBlockingError) {
                        console.error('[MANUAL_REFRESH] BLOCKING errors found, stopping refresh');
                        const blockingErrors = progressData.validation_errors.filter(e => e.blocking);
                        const errorMessage = blockingErrors.map(e => e.message).join('; ');
                        showNotification(`Claims validation failed: ${errorMessage}`, 'refresh-terms');
                        termsStore.setAnalyzingTerms(false);
                        termProgressVisible = false;

                        // Update the claims editor with validation errors
                        if (window.updateClaimsValidationErrors) {
                            window.updateClaimsValidationErrors(progressData.validation_errors);
                        }
                        return;
                    }
                }
                
                // Update terms progressively as they arrive (only for streaming responses)
                if (progressData.term_counts && Object.keys(progressData.term_counts).length > 0 && !progressData.isComplete) {
                    claimsTermService.updateTermsProgressive(progressData.term_counts, claims, progressData);
                }
            }
        });

        // Check if the analysis was cancelled
        if (result === null) {
            console.log('[MANUAL_REFRESH] Analysis was cancelled by user');
            return;
        }

        // Final update when complete
        console.log('[MANUAL_REFRESH] Final update - Backend response:', result);
        console.log('[MANUAL_REFRESH] Result keys:', Object.keys(result || {}));
        console.log('[MANUAL_REFRESH] Result.term_counts exists:', !!result?.term_counts);
        console.log('[MANUAL_REFRESH] Result.term_counts keys:', result?.term_counts ? Object.keys(result.term_counts) : 'N/A');
        console.log('[MANUAL_REFRESH] Result.terms exists:', !!result?.terms);
        console.log('[MANUAL_REFRESH] Result.validation_errors:', result?.validation_errors);
        
        // Check for validation errors in the final result
        if (result?.validation_errors) {
            console.log('[MANUAL_REFRESH] Final validation errors:', result.validation_errors);
            // Update the claims editor with validation errors
            if (window.updateClaimsValidationErrors) {
                window.updateClaimsValidationErrors(result.validation_errors);
            }
        }
        
        if (result?.term_counts) {
            console.log('[MANUAL_REFRESH] Using term_counts structure');
            // Use the claimsTermService to handle the new structure
            claimsTermService.updateTerms(result.term_counts, claims, result);
        } else if (result?.terms) {
            console.log('[MANUAL_REFRESH] Using terms structure');
            // Fallback for old response structure
            claimsTermService.updateTerms(result.terms, claims, result);
        } else {
            // No terms returned, but still need to clear analyzing state
            console.log('[MANUAL_REFRESH] No terms returned, clearing analyzing state');
            termsStore.setAnalyzingTerms(false);
        }

        // Hide progress bar on success
        termProgressVisible = false;
    } catch (error) {
        console.error('[DEBUG] Error refreshing terms:', error);
        console.error('[DEBUG] Error stack:', error.stack);
        console.error('[DEBUG] Error type:', error.constructor.name);
        showNotification('Error refreshing terms', 'refresh-terms');
        termsStore.setAnalyzingTerms(false);
        termProgressVisible = false;
    }
}

$: {
    console.log('TermsPanel - Term entries updated:', {
        termEntries: termEntries.map(([key, data]) => ({ key, term: data.parent_term, claim: data.claim_num })),
        selectedEntries: selectedEntries ? Array.from(selectedEntries) : [],
        highlightedEntries: highlightedEntries ? Array.from(highlightedEntries) : []
    });
}

// Note: Child terms are now provided by the main backend analysis with deduplication
// No need to proactively fetch child terms since they come with the parent terms


function toggleEntry(indexedKey) {
    termsStore.toggleSelected(indexedKey);
    
    // Trigger auto-scroll if term is being selected (not deselected)
    const selectedEntries = $termsStore.selectedEntries;
    if (selectedEntries && selectedEntries.has(indexedKey)) {
        const termData = $termsStore.termEntries.get(indexedKey);
        if (termData) {
            // Use a small delay to ensure the highlight is applied first
            setTimeout(() => {
                termAutoScrollService.scrollToTerm(termData);
            }, 50);
        }
    }
}

function isHighlighted(indexedKey) {
    return $termsStore.highlightedEntries.has(indexedKey);
}

/**
 * Check if a term has any errors (article errors or parent term warnings)
 * @param {Object} termData - Term data object from the store
 * @returns {boolean} true if the term has errors
 */
function hasErrors(termData) {
    // Check for parent term warnings
    if (termData._warning && termData._warning.type === 'prefix_warning') {
        return true;
    }
    
    // Check for article errors in child terms
    if (termData.children) {
        for (const [childTerm, childData] of Object.entries(termData.children)) {
            if (childData.references && childData.references.errors) {
                // Check if any error exists in the errors array
                const hasArticleError = childData.references.errors.some(error => 
                    error && (error.type === 'missing_article' || error.type === 'incorrect_article')
                );
                if (hasArticleError) {
                    return true;
                }
            }
        }
    }
    
    return false;
}

function startEditing(indexedKey, event) {
    event.stopPropagation();
    console.log(`[TermsPanel] Starting to edit key: ${indexedKey}`);
    // Set the editing value from the current term
    const termData = $termsStore.termEntries.get(indexedKey);
    if (termData) {
        editingValue = termData.parent_term;
        console.log(`[TermsPanel] Set editingValue to: "${editingValue}"`);
    }
    termsStore.startEditing(indexedKey);
}

function handleKeyDown(event, indexedKey, termData) {
    console.log(`[TermsPanel] Key pressed: ${event.key}, isSaving: ${isSaving}`);
    if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        
        // Prevent multiple saves
        if (isSaving) {
            console.log(`[TermsPanel] Already saving, ignoring Enter key`);
            return;
        }
        
        console.log(`[TermsPanel] Enter key detected, saving...`);
        saveTerm(event, indexedKey, termData);
    } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        termsStore.stopEditing();
    }
}

async function saveTerm(event, indexedKey, termData) {
    event.preventDefault();
    
    // Immediately stop editing to remove textarea from DOM
    termsStore.stopEditing();
    
    // Blur the textarea to remove focus
    if (event.target && event.target.blur) {
        event.target.blur();
    }
    
    isSaving = true; // Prevent blur from interfering
    
    const oldTerm = termData.parent_term;
    const newTerm = editingValue.trim();
    
    console.log(`[TermsPanel] Save term called for ${indexedKey}: ${oldTerm} -> ${newTerm}, isSaving now: ${isSaving}`);
    
    if (newTerm && newTerm !== oldTerm) {
        try {
            console.log(`[TermsPanel] Modifying term with persistent service: ${oldTerm} -> ${newTerm}`);
            
            // Use persistent terms service for text modification
            const claims = $claimsStore.content;
            const result = await persistentTermsService.modifyTermInText(oldTerm, newTerm, claims);
            
            console.log(`[TermsPanel] Persistent modification result:`, result);
            
            const childTerms = result.text_changes?.child_terms_replaced || [];
            if (childTerms.length > 0) {
                console.log(`[TermsPanel] Term "${oldTerm}" and ${childTerms.length} child terms modified to "${newTerm}" successfully (${result.text_changes?.replacements_made || 0} total replacements)`);
                console.log(`[TermsPanel] Child terms absorbed: ${childTerms.join(', ')}`);
            } else {
                console.log(`[TermsPanel] Term "${oldTerm}" modified to "${newTerm}" successfully (${result.text_changes?.replacements_made || 0} replacements)`);
            }
            
        } catch (error) {
            console.error(`[TermsPanel] Error modifying term "${oldTerm}" -> "${newTerm}":`, error);
            // TODO: Show user-friendly error notification
            alert(`Error modifying term "${oldTerm}": ${error.message}`);
        }
    } else {
        console.log('[TermsPanel] No update needed - terms are the same or new term is empty');
    }
    
    isSaving = false;
    console.log(`[TermsPanel] Save complete, isSaving set to false`);
}

function handleBlur() {
    console.log(`[TermsPanel] Blur event, isSaving: ${isSaving}`);
    // Only stop editing if we're not in the middle of saving
    if (!isSaving) {
        console.log(`[TermsPanel] Stopping editing due to blur`);
        termsStore.stopEditing();
    } else {
        console.log(`[TermsPanel] Ignoring blur because we're saving`);
    }
}

const focusOnMount = (node) => {
    console.log(`[TermsPanel] focusOnMount called for node:`, node);
    node.focus();
    console.log(`[TermsPanel] Focus set, activeElement:`, document.activeElement);
    // Auto-resize the textarea to fit content
    autoResizeTextarea(node);
};

// Auto-resize textarea based on content
function autoResizeTextarea(textarea) {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Set the height to match the content
    textarea.style.height = textarea.scrollHeight + 'px';
    
    // Adjust width based on content
    // Create a temporary element to measure text width
    const temp = document.createElement('span');
    temp.style.position = 'absolute';
    temp.style.visibility = 'hidden';
    temp.style.whiteSpace = 'pre';
    temp.style.font = window.getComputedStyle(textarea).font;
    temp.style.padding = window.getComputedStyle(textarea).padding;
    temp.textContent = textarea.value || textarea.placeholder || '';
    
    document.body.appendChild(temp);
    const textWidth = temp.offsetWidth;
    document.body.removeChild(temp);
    
    // Set width with some extra space for cursor
    textarea.style.width = Math.max(textWidth + 20, 60) + 'px';
}

/**
 * Handles term deletion when the delete button is clicked using persistent service
 * This will persist the deletion through refreshes by text pattern matching
 * 
 * Includes a confirmation dialog to prevent accidental deletions.
 */
async function deleteEntry(indexedKey, termData, event) {
    event.stopPropagation();
    event.preventDefault();
    
    const term = termData.parent_term;
    const claimNum = termData.claim_num;
    
    // Confirm deletion with the user
    if (confirm(`Are you sure you want to delete the term "${term}"?\n\nThis will persistently remove it from all future term analyses.`)) {
        try {
            console.log(`[TermsPanel] Deleting term persistently: ${term} (Claim ${claimNum})`);
            
            // Use persistent terms service for deletion
            const claims = $claimsStore.content;
            const result = await persistentTermsService.deleteTermByText(term, claims, claimNum);
            
            console.log(`[TermsPanel] Persistent deletion result:`, result);
            
            console.log(`[TermsPanel] Term "${term}" deleted persistently - will not reappear on refresh`);
            
        } catch (error) {
            console.error(`[TermsPanel] Error deleting term "${term}":`, error);
            // TODO: Show user-friendly error notification
            alert(`Error deleting term "${term}": ${error.message}`);
        }
    }
}

/**
 * Handles keyboard navigation for the delete button
 * Allows users to delete terms using keyboard (Enter or Space)
 * for improved accessibility
 */
function handleDeleteKeyDown(event, indexedKey, termData) {
    // Handle keyboard interaction for delete button
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        deleteEntry(indexedKey, termData, event);
    }
}


/**
 * Toggle expanded state for a term (child terms already fetched)
 * @param {string} term - Term to toggle expansion for
 * @param {Event} event - Click event
 */
async function toggleExpand(indexedKey, event) {
    event.stopPropagation(); // Prevent term selection
    
    // Toggle expanded state for this indexed entry
    termsStore.toggleExpanded(indexedKey);
    
    // Blur the button to remove focus state after clicking
    event.currentTarget.blur();
}

/**
 * Handle keyboard interaction for the expand button
 * @param {KeyboardEvent} event - Keyboard event
 * @param {string} indexedKey - Indexed key to toggle expansion for
 */
function handleExpandKeyDown(event, indexedKey) {
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleExpand(indexedKey, event);
    }
}

</script>

<svelte:window on:click={handleClickOutside} on:keydown={handleKeydown} />

<div class="terms-panel" data-testid="terms-panel">
    <div class="panel-header-container">
        <h2 class="panel-header">Terms</h2>
    </div>
    
    <!-- Terms-specific toolbar -->
    <div class="terms-toolbar">
        <div class="toolbar-content">
            <div class="term-button-container">
                <button 
                    bind:this={addButtonElement}
                    class="toolbar-button terms-button"
                    class:active={isTermAddingMode}
                    on:click={() => {
                        console.log('[TermsPanel] Add button clicked, current mode:', isTermAddingMode);
                        
                        // First, check if there's an existing selection that can be processed
                        if (manualTermsServiceInstance && manualTermsServiceInstance.processExistingSelection()) {
                            console.log('[TermsPanel] Existing selection was processed');
                            // Selection was processed, no need to toggle mode
                            return;
                        }
                        
                        // No selection found, toggle term adding mode
                        console.log('[TermsPanel] No existing selection, toggling term adding mode');
                        termsStore.toggleTermAddingMode();
                    }}
                    title={isTermAddingMode ? "Cancel adding term" : "Add term from claims"}
                    aria-pressed={isTermAddingMode}
                    data-testid="add-term-button"
                >
                    <i class="fas {isTermAddingMode ? 'fa-times' : 'fa-plus'}"></i>
                    <span class="button-text">{isTermAddingMode ? 'Cancel' : 'Add'}</span>
                </button>
                {#if isTermAddingMode}
                    <div 
                        class="notification-bubble bubble-variant-terms custom-positioned-notification"
                        style={notificationStyle}
                        role="tooltip"
                        aria-live="polite"
                    >
                        Term Selection Mode Active
                    </div>
                {/if}
            </div>
            
            <div class="sort-menu">
                <button 
                    class="toolbar-button terms-button"
                    on:click={handleSortButtonClick}
                    title="Sort terms"
                    aria-expanded={showSortMenu}
                    aria-haspopup="true"
                    data-testid="sort-terms-button"
                >
                    <i class="fas fa-sort"></i>
                    <span class="button-text">Sort</span>
                </button>
                {#if showSortMenu}
                    <div 
                        class="sort-dropdown" 
                        transition:slide|local
                        role="menu"
                        tabindex="-1"
                    >
                        <button 
                            class="menu-item {currentSortMode === 'appearance' ? 'active' : ''}" 
                            on:click={() => {
                                handleSortTerms('appearance');
                                showSortMenu = false;
                            }}
                            role="menuitem"
                            aria-current={currentSortMode === 'appearance' ? 'true' : 'false'}
                            data-testid="sort-by-appearance-button"
                        >
                            <i class="fas fa-list-ol"></i>
                            By 1st appearance
                            {#if currentSortMode === 'appearance'}
                                <i class="fas fa-check menu-item-check"></i>
                            {/if}
                        </button>
                        <button 
                            class="menu-item {currentSortMode === 'alphabetical' ? 'active' : ''}" 
                            on:click={() => {
                                handleSortTerms('alphabetical');
                                showSortMenu = false;
                            }}
                            role="menuitem"
                            aria-current={currentSortMode === 'alphabetical' ? 'true' : 'false'}
                            data-testid="sort-alphabetical-button"
                        >
                            <i class="fas fa-sort-alpha-down"></i>
                            By alphabet
                            {#if currentSortMode === 'alphabetical'}
                                <i class="fas fa-check menu-item-check"></i>
                            {/if}
                        </button>
                    </div>
                {/if}
            </div>

            <ViewFilterDropdown />

            <div class="button-with-notification refresh-button-container">
                <button 
                    class="toolbar-button terms-button"
                    on:click={() => {
                        console.log('[DEBUG] Refresh button clicked from TermsPanel!');
                        handleRefreshTerms();
                    }}
                    disabled={hasActiveOperations}
                    title="{hasActiveOperations ? 'Processing terms...' : 'Refresh terms from claims'}"
                    data-testid="refresh-terms-button"
                >
                    {#if hasActiveOperations}
                        <i class="fas fa-spinner fa-spin"></i>
                    {:else}
                        <i class="fas fa-sync-alt"></i>
                    {/if}
                    <span class="button-text">{hasActiveOperations ? 'Thinking...' : 'Refresh'}</span>
                </button>
                <NotificationBubble 
                    show={currentNotification && notificationSource === 'refresh-terms'}
                    message={currentNotification || ''}
                    variant="error"
                    position="bottom"
                    duration={NOTIFICATION_TIMEOUT_MS}
                    on:hide={() => {
                        currentNotification = null;
                        notificationSource = null;
                    }}
                />
            </div>
        </div>
    </div>
    
    {#if isTermAddingMode}
        <div class="term-adding-indicator">
            Select text in the Claims editor to add as a term
        </div>
    {/if}
    
    {#if currentViewFilter !== 'all' && filteredTermCount < totalTermCount}
        <div class="filter-status">
            Showing {filteredTermCount} of {totalTermCount} terms
            {#if filteredTermCount === 0}
                <span class="no-results">• No terms match the current filter</span>
            {/if}
        </div>
    {/if}
    
    <div class="terms-list">
        {#each termEntries as [indexedKey, termData] (indexedKey)}
            <div class="term-container" data-testid="term-{indexedKey}" in:fade={{ duration: 200 }} out:scale={{ duration: 200 }}>
                {#if editingEntry === indexedKey}
                    <textarea
                        bind:value={editingValue}
                        class="term-edit-input"
                        on:keydown={(e) => handleKeyDown(e, indexedKey, termData)}
                        on:keyup={(e) => console.log(`[TermsPanel] KeyUp: ${e.key}`)}
                        on:blur={handleBlur}
                        on:input={(e) => autoResizeTextarea(e.target)}
                        use:focusOnMount
                        rows="1"
                    />
                {:else}
                    <div class="term-wrapper">
                        <div class="term-header">
                            <button 
                                class={`term-button ${selectedEntries.has(indexedKey) ? 'selected' : ''} ${termData.temporary ? 'temporary' : ''} ${termData.loading ? 'loading' : ''} ${termData.error ? 'error' : ''} ${termData.needs_refresh ? 'needs-refresh' : ''} ${termData.recently_modified ? 'recently-modified' : ''} ${termData.updating ? 'updating' : ''} ${hasErrors(termData) ? 'has-errors' : ''}`}
                                on:click={() => toggleEntry(indexedKey)}
                                aria-pressed={selectedEntries.has(indexedKey)}
                                disabled={termData.updating || termData.loading}
                                title={`${termData.parent_term} (${indexedKey})${termData.loading ? ' - Adding term...' : termData.error ? ' - Failed to add term' : termData.updating ? ` - Updating from "${termData.updating_from}"...` : termData.temporary ? ' - Pending analysis' : termData.recently_modified ? ' - Please refresh terms for best results' : termData.needs_refresh ? ' - References need refresh' : ''}`}
                            >
                                {#if termData.loading}
                                    <span class="loading-spinner"></span>
                                {/if}
                                <span class="term-text">{termData.parent_term}</span>
                                {#if termData.loading}
                                    <span class="claim-reference loading-text">{termData.loadingMessage || 'Adding...'}</span>
                                {:else if termData.error}
                                    <span class="claim-reference error-text">Failed to add</span>
                                {:else if termData.updating}
                                    <span class="claim-reference updating">Updating from "{termData.updating_from}"...</span>
                                {:else if termData.temporary && !termData.loading}
                                    <span class="claim-reference">Analyzing...</span>
                                {:else if termData.recently_modified}
                                    <span class="claim-reference recently-modified">Please refresh terms</span>
                                {:else if termData.needs_refresh}
                                    <span class="claim-reference needs-refresh">References need refresh</span>
                                {:else if showClaimReferences && termData.claim_num}
                                    <span class="claim-reference">Claim {termData.claim_num}</span>
                                {/if}
                            </button>
                            <button 
                                class="edit-bubble"
                                on:click={(e) => startEditing(indexedKey, e)}
                                on:keydown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        startEditing(indexedKey, e);
                                    }
                                }}
                                disabled={termData.updating}
                                aria-label={`Edit ${termData.parent_term}`}
                                tabindex="0"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="12">
                                    <path fill="currentColor" d="M20.71 7.04c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.37-.39-1.02-.39-1.41 0l-1.84 1.83 3.75 3.75M3 17.25V21h3.75L17.81 9.93l-3.75-3.75L3 17.25z"/>
                                </svg>
                            </button>
                            <button 
                                class="delete-button"
                                on:click={(e) => deleteEntry(indexedKey, termData, e)}
                                on:keydown={(e) => handleDeleteKeyDown(e, indexedKey, termData)}
                                aria-label={`Delete ${termData.parent_term}`}
                                tabindex="0"
                                title="Delete term"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="10" height="10">
                                    <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
                                </svg>
                            </button>
                            <!-- Only show expand button if this entry has child terms -->
                            {#if termData.children && Object.keys(termData.children).length > 0}
                                <button
                                    class="expand-button"
                                    on:click={(e) => toggleExpand(indexedKey, e)}
                                    on:keydown={(e) => handleExpandKeyDown(e, indexedKey)}
                                    aria-expanded={expandedEntries.has(indexedKey)}
                                    aria-label={expandedEntries.has(indexedKey) ? `Collapse ${termData.parent_term}` : `Expand ${termData.parent_term} to show child terms`}
                                    tabindex="0"
                                    title={expandedEntries.has(indexedKey) ? `Collapse child terms` : `Show child terms`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="10" height="10">
                                        <path fill="currentColor" d={expandedEntries.has(indexedKey) 
                                            ? "M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" 
                                            : "M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"} />
                                    </svg>
                                </button>
                            {/if}
                        </div>
                        
                        {#if expandedEntries.has(indexedKey)}
                            <div class="term-thread-container" data-testid="term-thread-container">
                                {#if termData.children && Object.keys(termData.children).length > 0}
                                    <ThreadContainer 
                                        items={Object.keys(termData.children)} 
                                        loading={false}
                                        parentAlignment={24}
                                        itemDistanceFromLine={16}
                                    />
                                {:else}
                                    <ThreadContainer 
                                        items={[]} 
                                        loading={true}
                                        parentAlignment={24}
                                        itemDistanceFromLine={16}
                                    />
                                {/if}
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        {/each}
    </div>

    <!-- Term progress bar -->
    <TermProgressBar
        isVisible={termProgressVisible}
        stepsRemaining={termProgressStepsRemaining}
        totalSteps={termProgressTotalSteps}
        estimatedTime={termProgressEstimatedTime}
        progress={termProgressPercent}
        on:stop={handleStopTermAnalysis}
    />
</div>

<style>
.terms-panel {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
}

.panel-header-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-2) var(--spacing-4);
}

.panel-header {
    margin: 0;
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    text-align: center;
    color: var(--color-panel-title-terms);
}

/* Terms-specific toolbar */
.terms-toolbar {
    position: relative;
    z-index: 5; /* Above panel content but below notifications */
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-2) var(--spacing-3);
    margin-bottom: var(--spacing-2);
    overflow: visible; /* Allow notification to extend outside */
    background-color: transparent; /* Same as panel background */
    border-bottom: 1px solid var(--color-terms-divider); /* Subtle terms divider */
}

.terms-toolbar .toolbar-content {
    display: flex;
    flex-wrap: nowrap; /* Keep buttons on one line by default */
    justify-content: center;
    align-items: center;
    overflow: visible; /* Allow notification to extend outside */
    gap: var(--spacing-1);
}

/* Adjust gap on smaller viewports */
@media (width <= 1600px) {
    .terms-toolbar .toolbar-content {
        gap: 6px;
    }
}

@media (width <= 1300px) {
    .terms-toolbar {
        padding: var(--spacing-2) var(--spacing-2);
    }
    
    .terms-toolbar .toolbar-content {
        gap: 4px;
    }
}

/* Only allow wrapping on very small screens */
@media (width <= 1200px) {
    .terms-toolbar .toolbar-content {
        flex-wrap: wrap;
    }
}

/* Toolbar button styles for terms panel */
.toolbar-button.terms-button {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
    padding: 0 var(--spacing-3);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    white-space: nowrap;
    color: var(--color-text-secondary);
    background-color: var(--color-btn-terms);
    border: none;
    border-radius: var(--border-radius-xl);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
    gap: 6px;
}

.toolbar-button.terms-button:hover {
    background-color: var(--color-btn-terms-hover);
    box-shadow: var(--shadow-sm);
    transform: translateY(-1px);
}

.toolbar-button.terms-button.active {
    color: var(--color-text-button);
    background-color: var(--color-terms-500);
    box-shadow: var(--shadow-terms-button-active);
}

.toolbar-button.terms-button:disabled {
    opacity: 0.7;
    transform: none;
    cursor: not-allowed;
}

/* Button text */
.button-text {
    transition: opacity 0.2s var(--ease-in-out);
}

/* Always hide Add and Refresh button text */
.term-button-container .button-text,
.button-with-notification .button-text {
    display: none;
}

.term-button-container .toolbar-button,
.button-with-notification .toolbar-button {
    min-width: auto;
    padding: 0 var(--spacing-2);
}

/* For very small viewports, make all buttons more compact */
@media (width <= 1300px) {
    .toolbar-button.terms-button {
        height: 28px;
        padding: 0 var(--spacing-2);
        font-size: var(--font-size-xs);
    }
    
    .toolbar-button.terms-button i {
        font-size: 14px;
    }
}

/* Sort menu styles */
.sort-menu {
    position: relative;
}

.sort-dropdown {
    position: absolute;
    top: calc(100% + var(--spacing-1));
    left: 50%;
    z-index: var(--z-index-dropdown);
    min-width: 180px;
    padding: var(--spacing-2);
    background-color: var(--color-bg-terms);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-dropdown);
    transform: translateX(-50%);
}

.menu-item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--spacing-2) var(--spacing-3);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    color: var(--color-text-secondary);
    background: transparent;
    border: none;
    border-radius: var(--border-radius-md);
    transition: all 0.2s var(--ease-in-out);
    cursor: pointer;
    gap: var(--spacing-2);
}

.menu-item:hover {
    background-color: var(--color-bg-hover-light);
}

.menu-item.active {
    background-color: var(--color-bg-hover-medium);
}

.menu-item i {
    width: var(--spacing-4);
}

.menu-item-check {
    margin-left: auto;
    color: var(--color-terms-600);
}

/* Button with notification wrapper */
.button-with-notification {
    position: relative;
}

.term-button-container {
    position: relative;
    z-index: 10; /* Ensure it's above other content */
}

/* Custom positioned notification that can overlay anywhere */
.custom-positioned-notification {
    position: relative;
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    
    /* Terms variant styling */
    color: var(--color-text-inverse);
    background-color: var(--color-terms-600);
    border-radius: var(--border-radius-md);
    box-shadow: var(--shadow-md);
    pointer-events: none;
}

/* Speech bubble arrow pointing down towards the button */
.custom-positioned-notification::after {
    position: absolute;
    top: 100%;
    left: 50%;
    width: 0;
    height: 0;
    border-top: 8px solid var(--color-terms-600);
    border-right: 8px solid transparent;
    border-left: 8px solid transparent;
    transform: translateX(-50%);
    content: '';
}

/* Refresh button no longer needs fixed width since it's always icon-only */

.term-adding-indicator {
    padding: var(--spacing-2) var(--spacing-3);
    margin: 0 var(--spacing-4) var(--spacing-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    background-color: var(--color-term-bg);
    border-left: 3px solid var(--color-terms-600);
}

.terms-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
    padding: var(--spacing-4);
    overflow-y: auto;
    gap: var(--spacing-2); 
}

.term-container {
    position: relative;
    display: inline-flex; /* Use inline-flex to size to content */
    flex-direction: column;
    max-width: 100%; /* Allow terms to fill the entire panel width when needed */
    margin-bottom: var(--spacing-4);
}

.term-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
}

.term-header {
    position: relative;
    z-index: 2;
    display: flex;
    justify-content: center;
}

.term-button {
    width: auto; /* Size to content */
    padding: calc(var(--spacing-1) * 1.6) calc(var(--spacing-2) * 1.6);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-snug);
    text-align: left; /* Left-align for better readability of long terms */
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word; /* Better word breaking for long terms */
    hyphens: auto; /* Allow hyphenation for very long words */
    color: var(--color-text-inverse);
    background-color: var(--color-terms-600);
    border: none;
    border-radius: var(--border-radius-2xl);
    box-shadow: var(--shadow-terms-sm);
    transition: all var(--duration-normal) var(--ease-in-out);
}

.term-button:hover {
    background-color: var(--color-terms-700);
    box-shadow: var(--shadow-terms-md);
    transform: translateY(-1px);
}

.term-button.selected {
    background-color: var(--color-terms-700);
    box-shadow: var(--shadow-terms-selected);
}

.term-button.temporary {
    background-color: var(--color-gray-400);
    opacity: 0.8;
}

.term-button.temporary:hover {
    background-color: var(--color-gray-500);
}

/* Loading state styles */
.term-button.loading {
    background: linear-gradient(135deg, 
        var(--color-terms-100) 0%, 
        var(--color-terms-200) 50%, 
        var(--color-terms-100) 100%);
    background-size: 200% 200%;
    border: 1px solid var(--color-terms-300);
    animation: shimmer 1.5s ease-in-out infinite;
    cursor: wait;
}

.term-button.loading:hover {
    background: linear-gradient(135deg, 
        var(--color-terms-100) 0%, 
        var(--color-terms-200) 50%, 
        var(--color-terms-100) 100%);
}

/* Error state styles */
.term-button.error {
    background-color: var(--color-error-100);
    border: 1px solid var(--color-error-300);
}

.term-button.error:hover {
    background-color: var(--color-error-200);
}

/* Loading spinner */
.loading-spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    margin-right: 6px;
    border: 2px solid var(--color-terms-300);
    border-top-color: var(--color-terms-600);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

/* Loading text styles */
.claim-reference.loading-text {
    color: var(--color-terms-600);
    font-style: italic;
}

.claim-reference.error-text {
    color: var(--color-error);
    font-style: italic;
}

/* Animations */
@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@keyframes shimmer {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}

.term-button.needs-refresh {
    background-color: var(--color-warning-bg);
    border: 1px solid var(--color-warning-border);
    animation: needs-refresh-pulse 2s infinite;
}

.term-button.needs-refresh:hover {
    background-color: var(--color-warning-bg-hover);
}

.term-button.recently-modified {
    background-color: var(--color-success-bg);
    border: 1px solid var(--color-success-border);
    animation: recently-modified-pulse 2s infinite;
}

.term-button.recently-modified:hover {
    background-color: var(--color-success-bg-hover);
}

.term-button.updating {
    background-color: var(--color-info-bg);
    border: 1px solid var(--color-info-border);
    opacity: 0.9;
    animation: updating-pulse 1.5s infinite;
    cursor: not-allowed;
}

.term-button.updating:hover {
    background-color: var(--color-info-bg);
    box-shadow: var(--shadow-terms-sm); /* Keep basic shadow */
    transform: none; /* Disable hover transform */
}

.term-button.has-errors {
    color: var(--color-error-text);
    background-color: var(--color-error-bg);
    border: 1px solid var(--color-error-border);
    box-shadow: 0 0 0 1px rgba(220, 38, 127, 0.15);
}

.term-button.has-errors:hover {
    color: var(--color-error-text);
    background-color: var(--color-error-bg-hover);
    border-color: var(--color-error-border-hover);
    box-shadow: 0 0 0 1px rgba(220, 38, 127, 0.2), var(--shadow-terms-md);
}

.term-button.has-errors.selected {
    color: var(--color-error-text);
    background-color: var(--color-error-bg-selected);
    border-color: var(--color-error-border-hover);
    box-shadow: 0 0 0 1px rgba(220, 38, 127, 0.25), var(--shadow-terms-selected);
}

.term-button.has-errors.selected:hover {
    color: var(--color-error-text);
    background-color: var(--color-error-bg-selected);
    border-color: var(--color-error-border-hover);
}

@keyframes pulse {
    0% { opacity: 0.8; }
    50% { opacity: 1; }
    100% { opacity: 0.8; }
}

@keyframes needs-refresh-pulse {
    0% { opacity: 0.85; }
    50% { opacity: 1; }
    100% { opacity: 0.85; }
}

@keyframes recently-modified-pulse {
    0% { opacity: 0.9; }
    50% { opacity: 1; }
    100% { opacity: 0.9; }
}

@keyframes updating-pulse {
    0% { opacity: 0.9; }
    50% { opacity: 1; }
    100% { opacity: 0.9; }
}

/* Dark mode text color adjustment for better readability on light blue background */
:global([data-theme="dark"]) .term-button:hover,
:global([data-theme="dark"]) .term-button.selected {
    color: var(--color-text-on-light);
}

/* Keep error text color consistent in dark mode */
:global([data-theme="dark"]) .term-button.has-errors,
:global([data-theme="dark"]) .term-button.has-errors:hover,
:global([data-theme="dark"]) .term-button.has-errors.selected,
:global([data-theme="dark"]) .term-button.has-errors.selected:hover {
    color: var(--color-error-text);
}

.claim-reference {
    display: block;
    margin-top: 2px;
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-normal);
    color: var(--color-text-secondary);
    opacity: 0.8;
}

.claim-reference.needs-refresh {
    font-weight: var(--font-weight-medium);
    color: var(--color-warning-text);
    animation: needs-refresh-text 2s infinite;
}

.claim-reference.recently-modified {
    font-weight: var(--font-weight-medium);
    color: var(--color-success-text);
    animation: recently-modified-text 2s infinite;
}

.claim-reference.updating {
    font-weight: var(--font-weight-medium);
    color: var(--color-info-text);
    animation: updating-text 1.5s infinite;
    font-style: italic;
}

@keyframes needs-refresh-text {
    0% { opacity: 0.7; }
    50% { opacity: 1; }
    100% { opacity: 0.7; }
}

@keyframes recently-modified-text {
    0% { opacity: 0.8; }
    50% { opacity: 1; }
    100% { opacity: 0.8; }
}

@keyframes updating-text {
    0% { opacity: 0.8; }
    50% { opacity: 1; }
    100% { opacity: 0.8; }
}

.edit-bubble {
    position: absolute;
    top: calc(var(--spacing-2) * -1);
    left: calc(var(--spacing-2) * -1);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    padding: 0;
    color: var(--color-text-secondary);
    background-color: var(--color-gray-300);
    border: none;
    border-radius: 50%;
    opacity: 0;
    box-shadow: var(--shadow-sm);
    transition: opacity var(--duration-normal) var(--ease-in-out);
    cursor: pointer;
}

.term-container:hover .edit-bubble {
    opacity: 1;
}

.edit-bubble:hover {
    background-color: var(--color-gray-400);
}

.edit-bubble:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.edit-bubble:disabled:hover {
    background-color: var(--color-gray-300);
}

.delete-button {
    position: absolute;
    top: calc(var(--spacing-2) * -1);
    right: calc(var(--spacing-2) * -1);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    padding: 0;
    color: var(--color-text-button);
    background-color: var(--color-error);
    border: none;
    border-radius: 50%;
    opacity: 0;
    box-shadow: var(--shadow-sm);
    transition: opacity var(--duration-normal) var(--ease-in-out);
    cursor: pointer;
}

.term-container:hover .delete-button {
    opacity: 1;
}

.delete-button:hover {
    background-color: var(--color-error-hover);
}

.delete-button:focus {
    outline: none;
    opacity: 1;
    box-shadow: var(--shadow-error-focus);
}

.term-edit-input {
    box-sizing: border-box;
    width: auto;
    min-width: 0;
    max-width: 100%;
    padding: calc(var(--spacing-1) * 1.6) calc(var(--spacing-2) * 1.6);
    overflow: hidden;
    font-family: inherit;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-snug);
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    color: var(--color-text-primary);
    background-color: var(--color-white);
    border: 2px solid var(--color-terms-600);
    border-radius: var(--border-radius-2xl);
    outline: none;
    resize: none;
}

.term-edit-input:focus {
    border-color: var(--color-terms-700);
    box-shadow: var(--shadow-focus-terms);
}

.expand-button {
    position: absolute;
    bottom: -10px;
    left: 50%;
    z-index: 2;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    font-size: 10px;
    color: var(--color-text-secondary);
    background-color: var(--color-white); /* Changed to white like other buttons */
    border: 1px solid var(--color-gray-300);
    border-radius: 50%;
    opacity: 0;
    box-shadow: var(--shadow-xs);
    transition: all var(--duration-normal) var(--ease-in-out);
    transform: translateX(-50%);
    cursor: pointer;
}

.term-container:hover .expand-button {
    opacity: 1;
}

.expand-button:hover {
    background-color: var(--color-gray-200);
    transform: translateX(-50%) scale(1.1);
}

.expand-button:focus {
    outline: none;
    box-shadow: var(--shadow-focus-terms);
}

/* Only show focused expand button when the container is hovered */
.term-container:hover .expand-button:focus {
    opacity: 1;
}

/* When expanded, style the button differently but keep hover behavior */
.expand-button[aria-expanded="true"] {
    color: var(--color-text-inverse);
    background-color: var(--color-terms-600);
    border-color: var(--color-terms-600);
    box-shadow: var(--shadow-terms-md);
}

/* Ensure expand button shows on hover even when expanded */
.term-container:hover .expand-button[aria-expanded="true"] {
    opacity: 1;
}

/* Thread styles moved to separate components */

/* See ThreadContainer.svelte, ThreadItem.svelte, and thread-styles.svelte */

.term-thread-container {
    position: relative;
    width: 100%; /* Take full width of term container */
    margin-top: var(--spacing-1);
    margin-bottom: 0;
}

/* Custom scrollbar */
:global(.terms-list::-webkit-scrollbar) {
    width: 6px;
    height: 6px;
}

:global(.terms-list::-webkit-scrollbar-track) {
    background: var(--color-scrollbar-track);
}

:global(.terms-list::-webkit-scrollbar-thumb) {
    background-color: var(--color-scrollbar-terms-thumb);
    border-radius: 3px;
}

:global(.terms-list::-webkit-scrollbar-thumb:hover) {
    background-color: var(--color-scrollbar-terms-thumb-hover);
}

/* Scrollbar for Firefox */
:global(.terms-list) {
    scrollbar-width: thin;
    scrollbar-color: var(--color-scrollbar-terms-thumb) var(--color-scrollbar-track);
}

/* Custom positioning for refresh button notification to prevent right-side cutoff */
.refresh-button-container :global(.notification-bubble.bubble-bottom) {
    left: 50%; /* Keep at center */
    transform: translateX(-75%); /* But shift the bubble more to the left */
}

/* Fix the arrow to point back to the center of the button */
.refresh-button-container :global(.notification-bubble.bubble-bottom::before) {
    left: 75%; /* Move arrow to compensate for shifted bubble */
}
  
/* Filter status bar */
.filter-status {
    padding: var(--spacing-2) var(--spacing-4);
    margin: 0 var(--spacing-4) var(--spacing-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    background-color: var(--color-bg-hover-light);
    border-radius: var(--border-radius-md);
    border-left: 3px solid var(--color-terms-500);
}

.filter-status .no-results {
    color: var(--color-text-muted);
    font-style: italic;
}
</style>
